import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, ShieldCheck, Hammer, LayoutDashboard } from "lucide-react";
import { exchangeSession, fetchMe, logout as apiLogout } from "@/lib/adminApi";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const setRobotsNoindex = () => {
  document.title = "Admin · StayFlow";
  let el = document.head.querySelector('meta[name="robots"]');
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "robots");
    document.head.appendChild(el);
  }
  el.setAttribute("content", "noindex, nofollow");
};

const Splash = ({ text }) => (
  <div className="min-h-screen bg-ink flex items-center justify-center" data-testid="admin-splash">
    <div className="flex items-center gap-3 text-white/70">
      <Loader2 className="w-5 h-5 animate-spin text-brand" />
      <span className="text-sm font-mono">{text}</span>
    </div>
  </div>
);

const Login = ({ error }) => (
  <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="admin-login">
    <div className="w-full max-w-sm">
      <div className="flex items-center gap-2.5 mb-10">
        <div className="w-10 h-10 rounded-xl bg-brand text-white flex items-center justify-center font-heading font-extrabold text-sm shadow-[0_8px_24px_rgba(255,26,26,.35)]">
          SF
        </div>
        <div>
          <p className="text-white font-heading font-bold leading-none">StayFlow</p>
          <p className="text-white/40 text-xs font-mono mt-1">Admin Console</p>
        </div>
      </div>

      <h1 className="text-white font-heading font-extrabold text-3xl tracking-tight">Restricted area</h1>
      <p className="mt-3 text-white/50 text-sm leading-relaxed">
        This console is limited to authorized StayFlow staff. Sign in with your approved Google account to continue.
      </p>

      {error && (
        <div className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="admin-login-error">
          {error}
        </div>
      )}

      <button
        onClick={startGoogleLogin}
        data-testid="admin-google-login"
        className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-white text-ink font-medium h-12 transition-transform duration-200 hover:-translate-y-0.5"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/>
          <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"/>
          <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z"/>
        </svg>
        Continue with Google
      </button>

      <p className="mt-6 flex items-center gap-2 text-white/30 text-xs font-mono">
        <ShieldCheck className="w-3.5 h-3.5" /> Access is logged and restricted to one account.
      </p>
    </div>
  </div>
);

const Dashboard = ({ user, onLogout }) => (
  <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
    <header className="sticky top-0 z-10 bg-ink text-white">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-brand text-white flex items-center justify-center font-heading font-extrabold text-xs">SF</div>
          <span className="font-heading font-bold">StayFlow</span>
          <span className="text-white/30 font-mono text-xs px-2 py-0.5 rounded-full border border-white/10">admin</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2.5">
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" />}
            <div className="hidden sm:block leading-tight">
              <p className="text-sm font-medium" data-testid="admin-user-name">{user?.name}</p>
              <p className="text-white/40 text-xs">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 text-white/80 text-sm px-3.5 py-2 hover:bg-white/5 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>
    </header>

    <main className="max-w-6xl mx-auto px-6 py-12">
      <div className="flex items-center gap-2 text-brand">
        <LayoutDashboard className="w-4 h-4" />
        <p className="overline">Console</p>
      </div>
      <h1 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl tracking-tight text-ink">
        Welcome back, {user?.name?.split(" ")[0] || "Admin"}.
      </h1>
      <p className="mt-3 text-ink-soft max-w-xl">
        This is your private workspace. It's empty for now — we'll build your tools here step by step and wire them to your app's API as we go.
      </p>

      <div className="mt-10 rounded-3xl border border-dashed border-black/15 bg-white/60 p-12 text-center" data-testid="admin-empty-state">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-brand/10 text-brand flex items-center justify-center">
          <Hammer className="w-6 h-6" />
        </div>
        <h2 className="mt-5 font-heading font-bold text-lg text-ink">Nothing here yet</h2>
        <p className="mt-2 text-sm text-ink-soft max-w-sm mx-auto">
          Tell me what you'd like to build first — bookings, listings, analytics, or anything else — and it'll appear right here.
        </p>
      </div>
    </main>
  </div>
);

const AdminApp = () => {
  const location = useLocation();
  const hasCallback = (location.hash || "").includes("session_id=");
  const [status, setStatus] = useState(hasCallback ? "callback" : "checking"); // checking | callback | authed | unauthed
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const ran = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      setStatus("authed");
    } catch {
      setStatus("unauthed");
    }
  }, []);

  useEffect(() => {
    setRobotsNoindex();
  }, []);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (hasCallback) {
      const sid = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
      (async () => {
        try {
          const me = await exchangeSession(sid);
          window.history.replaceState(null, "", "/admin");
          setUser(me);
          setStatus("authed");
          toast.success("Signed in");
        } catch (e) {
          window.history.replaceState(null, "", "/admin");
          const s = e?.response?.status;
          setError(
            s === 403
              ? "That Google account isn't authorized to access this area."
              : "Sign-in failed. Please try again."
          );
          setStatus("unauthed");
        }
      })();
    } else {
      checkAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    try {
      await apiLogout();
    } catch (e) {
      console.warn("logout error", e);
    }
    setUser(null);
    setError("");
    setStatus("unauthed");
  };

  if (status === "checking" || status === "callback") return <Splash text="Checking access…" />;
  if (status === "unauthed") return <Login error={error} />;
  return <Dashboard user={user} onLogout={handleLogout} />;
};

export default AdminApp;
